import type { CurrencyRecord, SearchEntry } from './currency-metadata';
import { POPULAR_CURRENCIES } from './currency-metadata';

/** Normalize for search: trim, lowercase, strip spaces/hyphens/underscores/dots. */
export function normalizeCurrencyQuery(value: string): string {
  return value.trim().toLowerCase().replace(/[\s\-_.]+/g, '');
}

function aliasNorm(alias: string): string {
  return alias.toLowerCase().replace(/[\s\-_.]+/g, '');
}

/** True when every char of `q` appears in `hay` in order (subsequence). */
function isSubsequence(q: string, hay: string): boolean {
  if (q.length === 0) return false;
  let i = 0;
  for (const ch of hay) {
    if (ch === q[i]) {
      i += 1;
      if (i === q.length) return true;
    }
  }
  return false;
}

/**
 * Build a search index over the metadata list. Pure, no network.
 * Precomputes the `_hay` substring haystack per entry.
 */
export function buildSearchIndex(list: readonly CurrencyRecord[]): SearchEntry[] {
  return list.map((record) => {
    const parts = [record.code, record.name, record.country ?? '', ...record.aliases];
    const hay = parts.map((p) => aliasNorm(p)).filter(Boolean).join(' ');
    return { ...record, _hay: hay };
  });
}

const POPULAR_RANK: Record<string, number> = Object.fromEntries(
  POPULAR_CURRENCIES.map((code, i) => [code, i]),
);

function tieRank(code: string): number {
  return POPULAR_RANK[code] ?? POPULAR_CURRENCIES.length;
}

/**
 * Score an entry against a normalized query. Lower is better; first match wins.
 * 0 exact code · 1 exact countryCode · 2 exact name · 3 exact country name ·
 * 4 code/name startsWith · 5 aliases.includes · 6 symbol===q · 7 substring ·
 * 8 fuzzy subsequence (only when nothing scored <= 7).
 */
function scoreEntry(entry: SearchEntry, q: string): number | null {
  const code = entry.code.toLowerCase();
  const name = aliasNorm(entry.name);
  const cc = (entry.countryCode ?? '').toLowerCase();
  const country = entry.country ? aliasNorm(entry.country) : '';
  if (q === code) return 0;
  if (cc !== '' && q === cc) return 1;
  if (q === name) return 2;
  if (country !== '' && q === country) return 3;
  if (code.startsWith(q) || name.startsWith(q)) return 4;
  for (const a of entry.aliases) {
    if (aliasNorm(a) === q) return 5;
  }
  if (entry.symbol !== null && q === entry.symbol.toLowerCase()) return 6;
  if (entry._hay.includes(q)) return 7;
  return null;
}

/**
 * Search the index. Pure, no network. Tie-break: popular first, then code alpha.
 */
export function searchCurrencies(
  query: string,
  index: readonly SearchEntry[],
  limit = 8,
): SearchEntry[] {
  const q = normalizeCurrencyQuery(query);
  if (q === '') return [];
  const scored: Array<{ entry: SearchEntry; score: number }> = [];
  for (const entry of index) {
    const score = scoreEntry(entry, q);
    if (score !== null) scored.push({ entry, score });
  }
  if (scored.length === 0) {
    // Tier 8: fuzzy subsequence fallback over code+name.
    for (const entry of index) {
      const target = aliasNorm(entry.code) + aliasNorm(entry.name);
      if (isSubsequence(q, target)) scored.push({ entry, score: 8 });
    }
  }
  scored.sort((a, b) => {
    if (a.score !== b.score) return a.score - b.score;
    const pa = tieRank(a.entry.code);
    const pb = tieRank(b.entry.code);
    if (pa !== pb) return pa - pb;
    return a.entry.code < b.entry.code ? -1 : a.entry.code > b.entry.code ? 1 : 0;
  });
  return scored.slice(0, Math.max(0, limit)).map((s) => s.entry);
}
